import { Col, Pagination, Row } from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import styled from "styled-components";
import moment from "moment";
import Table from "../../../../components/atoms/Table";
import Modal from "../../../../components/molecures/Modals/Modal";
import useModals from "../../../../hooks/useModals";
import { ReactComponent as ErrorIcon } from "../../../../assets/icons/Caution24.svg";
import { ReactComponent as DotSmall } from "../../../../assets/icons/DotSmall.svg";
import { ReactComponent as FilterSet } from "../../../../assets/icons/FilterSet.svg";
import { ReactComponent as FilterUnset } from "../../../../assets/icons/FilterUnset.svg";
import { WarningRepair } from "../../../../model/WarningRepair";
import { HttpError } from "../../../../network/types";
import { alertError } from "../../../../shared/utils";
import { theme } from "../../../../theme";
import { checkRepairShopBusiness } from "../../../../apis/ReservationConfig/apis";
import { CheckRepairShopBusinessRequestBody } from "../../../../apis/ReservationConfig/apis/types";
import { StyledModalContainer } from "../../styles";
import { RepairShopSortType } from "../../../../shared/enums";

interface Props {
  preRequestBody?: CheckRepairShopBusinessRequestBody;
}

const CheckAvailableModal: React.FC<Props> = ({ preRequestBody }) => {
  const { resetModal } = useModals();
  const [datas, setDatas] = useState<WarningRepair[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isSort, setIsSort] = useState<boolean>(true);
  const checkReservationAvailableMutation = useMutation(
    checkRepairShopBusiness,
    {
      onSuccess: (res) => {
        setDatas(res.repairList);
        setCount(res.count);
      },
      onError: (err: HttpError) => {
        if (err.status === 404) {
          setDatas([]);
          setCount(0);
        } else alertError(err.message);
      },
    }
  );

  const onSort = () => {
    if (preRequestBody)
      checkReservationAvailableMutation.mutate({
        ...preRequestBody,
        repairSortType: isSort
          ? RepairShopSortType.WARNING
          : RepairShopSortType.ALL,
      });
    setIsSort(!isSort);
  };
  const columns: ColumnsType<WarningRepair> = [
    {
      dataIndex: "repairNo",
      title: "주문번호",
      ellipsis: true,
      render: (value, record) =>
        record.isWarningRepair ? <StyledRedSpan>{value}</StyledRedSpan> : value,
    },

    {
      dataIndex: "wishedAt",
      title: () => {
        return (
          <StyledSortColumn>
            예약일시
            <StyledIconWrapper onClick={onSort}>
              {isSort ? <FilterSet /> : <FilterUnset />}
            </StyledIconWrapper>
          </StyledSortColumn>
        );
      },
      render: (value, record) =>
        record.isWarningRepair ? (
          <StyledRedSpan>
            {moment(record.reservedAt).format("YYYY/MM/DD HH:mm:ss")}
          </StyledRedSpan>
        ) : (
          moment(record.reservedAt).format("YYYY/MM/DD HH:mm:ss")
        ),
    },

    {
      dataIndex: "customerName",
      title: "고객명",
      render: (value, record) =>
        record.isWarningRepair ? <StyledRedSpan>{value}</StyledRedSpan> : value,
    },
    {
      dataIndex: "customerVehicleNo",
      title: "차량번호",
      render: (value, record) =>
        record.isWarningRepair ? <StyledRedSpan>{value}</StyledRedSpan> : value,
    },
  ];
  useEffect(() => {
    if (preRequestBody)
      checkReservationAvailableMutation.mutate(preRequestBody);
  }, []);
  return (
    <Modal
      width={700}
      title="영업시간 변경확인"
      onOk={() => {
        resetModal();
      }}
      onAdditional={resetModal}
      onCancel={resetModal}
      okText="닫기"
      okButtonProps={{ ghost: true, type: "default" }}
      isOneBtn
    >
      <StyledModalContainer>
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <StyledNoticeText>
              <StyledNotice>
                <ErrorIcon style={{ marginRight: "4px" }} />
                <StyledRedSpan fontweight={700}>
                  기존 예약 건이 존재하여 예약 시간을 변경할 수 없습니다.
                </StyledRedSpan>
              </StyledNotice>
              <Row wrap={false}>
                <Col>
                  <DotSmall />
                </Col>
                <Col>
                  수정시간과 일치하지 않는 기존 예약이 존재합니다. 고객과
                  협의하여 영업가능 시간으로 장착 예약시간을 변경하거나,
                  고객센터로 연락하여 다른 매장으로 변경해주세요.
                </Col>
              </Row>
              <Row wrap={false}>
                <Col>
                  <DotSmall />
                </Col>
                <Col>
                  <StyledSpan> 타이어픽 고객센터 (1600-7013) </StyledSpan>
                </Col>
              </Row>
            </StyledNoticeText>
          </Col>
          <Col span={24}>
            <Table
              bordered
              scroll={{ y: "calc(48px * 4 + 24px)" }}
              footer={() => (
                <StyledPaginationWrapper>
                  <div>
                    총<StyledTotalCount>{count}</StyledTotalCount>
                  </div>
                  <Pagination
                    onChange={(e) => {
                      if (preRequestBody)
                        checkReservationAvailableMutation.mutate({
                          ...preRequestBody,
                          page: e,
                          pageSize: 10,
                        });
                    }}
                  />
                </StyledPaginationWrapper>
              )}
              pagination={false}
              dataSource={datas}
              columns={columns}
            />
          </Col>
        </Row>
      </StyledModalContainer>
    </Modal>
  );
};
const StyledNoticeText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px;
  background: ${theme.colors.blue5};
  border-radius: 8px;
`;

const StyledRedSpan = styled.span<{ fontweight?: number }>`
  font-weight: ${({ fontweight }) => fontweight || 400};
  color: #e60000;
`;
const StyledSpan = styled.span`
  font-weight: 700;
`;
const StyledTotalCount = styled.span`
  ${theme.typo.heading.b14};
  margin-left: 4px;
`;
const StyledPaginationWrapper = styled.div`
  display: flex;
  padding: 4px;
  align-items: center;
  justify-content: space-between;
`;
const StyledNotice = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;
const StyledSortColumn = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const StyledIconWrapper = styled.div`
  height: 22px;
  cursor: pointer;
`;
export default CheckAvailableModal;
